// Zustand store for checkout state management

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { CustomShippingOption, ThemeConfig, CheckoutCompleteResult } from '../types/config';

// =====================================================
// TYPES
// =====================================================

export type CheckoutStep = 'identity' | 'address' | 'shipping' | 'payment';

export interface CustomerData {
  email: string;
  /** Display form: "Anne Lise Hansen". Kept for labels and the SDK. */
  name: string;
  /**
   * The two fields the customer actually filled in.
   *
   * `name` alone is lossy: the address form asks for first and last name
   * separately, joined them with a space, and every consumer downstream had to
   * guess where the split was. "Anne Lise Hansen" came back as first name
   * "Anne", surname "Lise Hansen" — the customer's own middle name promoted to
   * part of their surname on the order and the receipt. Carry both forms.
   */
  firstName?: string;
  lastName?: string;
  phone: string;
}

export interface Address {
  id?: string;
  fullName?: string;
  phone?: string;
  /** "c/o Kari Nordmann" — collected by the address step, so modelled here. */
  careOf?: string;
  streetAddress: string;
  /** Apartment, entrance, floor. Optional everywhere it is read. */
  addressLine2?: string;
  postalCode: string;
  city: string;
  countryCode: string;
  isDefault?: boolean;
}

export interface ShippingOption {
  id: string;
  provider: string;
  method: string;
  displayName: string;
  description: string;
  price: { amount: number; currency: string };
  deliveryDays: number;
  estimatedDelivery?: { date: string; display: string };
  isPickup: boolean;
  isCheapest?: boolean;
  isFastest?: boolean;
  logoUrl?: string;
}

export interface PaymentMethod {
  type: string;
  displayName: string;
  iconUrl: string;
  isAvailable: boolean;
  isLastUsed?: boolean;
  isRecommended?: boolean;
  savedCard?: {
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
  };
}

export interface CartItem {
  id: string;
  name: string;
  quantity: number;
  unitPrice: { amount: number; currency: string };
  imageUrl?: string;
}

export interface CartData {
  items: CartItem[];
  totalAmount: { amount: number; currency: string };
  totalItems: number;
}

export interface MerchantConfig {
  merchantId: string;
  merchantName: string;
  logoUrl?: string;
  primaryColor?: string;
  features: {
    enableQuickPay: boolean;
    enableAddressSaving: boolean;
    enableVipps: boolean;
    collectPhone: boolean;
  };
  storeInfo?: {
    storeId: string;
    name: string;
    email?: string;
    phone?: string;
    merchantWebshopURL?: string;
    termsAndConditionsURL?: string;
    privacyPolicyURL?: string;
  };
  // Activated payment methods from Surfboard API
  activatedPaymentMethods?: string[];
}

// =====================================================
// CHECKOUT RESULT
// =====================================================

export interface PaymentResult {
  success: boolean;
  paymentId: string;
  orderId: string;
  status: 'COMPLETED' | 'PENDING' | 'FAILED' | 'CANCELLED';
  paymentMethod: string;
  errorMessage?: string;
  card?: {
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
  };
}

// =====================================================
// STORE STATE
// =====================================================

interface CheckoutState {
  // Session
  sessionId: string | null;
  surfboardOrderId: string | null;
  surfboardNonce: string | null;
  publicKey: string | null;
  
  // Steps
  currentStep: CheckoutStep;
  completedSteps: Set<CheckoutStep>;
  
  // Data
  customer: CustomerData | null;
  shippingAddress: Address | null;
  billingAddress: Address | null;
  useSameAddress: boolean;
  selectedShipping: ShippingOption | null;
  selectedPaymentMethod: PaymentMethod | null;
  
  // Available options
  shippingOptions: ShippingOption[];
  paymentMethods: PaymentMethod[];
  savedAddresses: Address[];
  
  // Custom shipping (from config)
  customShippingOptions: CustomShippingOption[];
  useBuiltInShipping: boolean;
  skipShipping: boolean;
  
  // Theming
  theme: ThemeConfig | null;
  
  // Cart
  cart: CartData | null;
  
  // Merchant
  merchant: MerchantConfig | null;
  
  // Locale / market (e.g. 'no', 'sv', 'da', 'fi', 'en')
  locale: string;
  
  // UI state
  isLoading: boolean;
  error: string | null;

  /**
   * A message to show on the payment step, set before that step mounts.
   *
   * Used when the customer comes back from Vipps or Klarna without having
   * paid: the explanation has to survive the step change and be rendered by
   * the payment step itself, which keeps its own error state.
   */
  paymentNotice: string | null;

  /**
   * What the customer has typed but not yet submitted.
   *
   * Nobody fills a checkout in one run — they reload, they come back later.
   * Field values only reached the store when a step was submitted, so a
   * refresh halfway through the address form emptied every field and the
   * customer had to type it all again.
   *
   * Deliberately separate from `customer` and `shippingAddress`: those decide
   * which step the checkout opens on, and half-typed values must never move
   * anyone past a step they have not completed.
   */
  draft: Record<string, string>;
  
  // Quick Pay
  hasQuickPay: boolean;
  deviceToken: string | null;
  
  // Preferences
  lastUsedPaymentMethod: string | null;
  
  // Payment result (for callback)
  paymentResult: PaymentResult | null;
}

interface CheckoutActions {
  // Session
  initSession: (sessionId: string, data: Partial<CheckoutState>) => void;
  setSurfboardData: (orderId: string, nonce: string, publicKey: string) => void;
  
  // Navigation
  setStep: (step: CheckoutStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  completeStep: (step: CheckoutStep) => void;
  
  // Customer
  setCustomer: (customer: CustomerData) => void;
  
  // Address
  setShippingAddress: (address: Address) => void;
  setBillingAddress: (address: Address) => void;
  setUseSameAddress: (same: boolean) => void;
  setSavedAddresses: (addresses: Address[]) => void;
  
  // Shipping
  setShippingOptions: (options: ShippingOption[]) => void;
  selectShipping: (option: ShippingOption) => void;
  setCustomShippingOptions: (options: CustomShippingOption[]) => void;
  setUseBuiltInShipping: (use: boolean) => void;
  setSkipShipping: (skip: boolean) => void;
  
  // Payment
  setPaymentMethods: (methods: PaymentMethod[]) => void;
  selectPaymentMethod: (method: PaymentMethod) => void;
  setPaymentResult: (result: PaymentResult) => void;
  
  // Theming
  setTheme: (theme: ThemeConfig) => void;
  
  // Cart
  setCart: (cart: CartData) => void;
  
  // Merchant
  setMerchant: (merchant: MerchantConfig) => void;
  
  // Locale
  setLocale: (locale: string) => void;
  
  // UI
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setPaymentNotice: (notice: string | null) => void;
  setDraft: (patch: Record<string, string>) => void;
  
  // Quick Pay
  setDeviceToken: (token: string | null) => void;
  setHasQuickPay: (has: boolean) => void;
  
  // Checkout complete - notify parent
  completeCheckout: (result: PaymentResult) => Promise<void>;
  
  // Reset
  reset: () => void;
}

// =====================================================
// INITIAL STATE
// =====================================================

const initialState: CheckoutState = {
  sessionId: null,
  surfboardOrderId: null,
  surfboardNonce: null,
  publicKey: null,
  
  currentStep: 'identity',
  completedSteps: new Set(),
  
  customer: null,
  shippingAddress: null,
  billingAddress: null,
  useSameAddress: true,
  selectedShipping: null,
  selectedPaymentMethod: null,
  
  shippingOptions: [],
  paymentMethods: [],
  savedAddresses: [],
  
  // Custom shipping defaults
  customShippingOptions: [],
  useBuiltInShipping: true,
  skipShipping: false,
  
  // Theme
  theme: null,
  
  cart: null,
  merchant: null,
  locale: 'no',
  
  isLoading: false,
  error: null,
  
  hasQuickPay: false,
  deviceToken: null,
  lastUsedPaymentMethod: null,
  
  paymentResult: null,
  paymentNotice: null,
  draft: {},
};

// =====================================================
// STEP ORDER (removed 'complete' - we just return data)
// =====================================================

const STEP_ORDER: CheckoutStep[] = ['identity', 'address', 'shipping', 'payment'];

// =====================================================
// POST MESSAGE HELPER
// =====================================================

export function notifyParent(type: string, data: unknown) {
  // Send to parent window (for iframe) - use document.referrer origin for security
  if (window.parent !== window) {
    try {
      // Determine parent origin from referrer or fallback to same-origin
      const parentOrigin = document.referrer 
        ? new URL(document.referrer).origin 
        : window.location.origin;
      window.parent.postMessage({ type: `SAGAPAY_${type}`, data }, parentOrigin);
    } catch {
      // Fallback: if referrer parsing fails, only post to same origin
      window.parent.postMessage({ type: `SAGAPAY_${type}`, data }, window.location.origin);
    }
  }
  
  // Also dispatch custom event (for inline widget)
  window.dispatchEvent(new CustomEvent(`sagapay:${type.toLowerCase()}`, { detail: data }));
  
  dispatchToConfig(type, data);
}

/**
 * Hand an event to the merchant's callbacks.
 *
 * Extracted because App.tsx has its own notifyParent — same name, different
 * body, transport only — and an event emitted through that one reached the
 * postMessage bridge and no callback at all. Two functions with one name is how
 * onReady came to fire never instead of too early.
 */
let readyDispatched = false;

export function dispatchToConfig(type: string, data: unknown) {
  const config = window.SagaPayCheckoutConfig;
  if (config) {
    // Ready happens once. Several places emit CHECKOUT_READY — some of them
    // meaning "and now this step" — and a merchant counting an analytics event
    // or starting a timer in onReady must not be told twice.
    if (type === 'CHECKOUT_READY') {
      if (readyDispatched) return;
      readyDispatched = true;
    }
    switch (type) {
      case 'CHECKOUT_COMPLETE':
        config.onComplete?.(data as any);
        break;
      case 'CHECKOUT_ERROR':
        config.onError?.(data as any);
        break;
      case 'CHECKOUT_CANCELLED':
        config.onCancel?.();
        break;
      case 'STEP_CHANGED':
        config.onStepChange?.((data as any).step);
        break;
      case 'CHECKOUT_READY':
        config.onReady?.();
        break;
    }
  }
}

// =====================================================
// STORE
// =====================================================

export const useCheckoutStore = create<CheckoutState & CheckoutActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        
        // Session
        initSession: (sessionId, data) => {
          // Get current session ID to check if this is a new session
          const currentSessionId = get().sessionId;
          const isNewSession = currentSessionId !== sessionId;
          
          // Always reset surfboard data when initializing a new session
          // This prevents old payment data from being reused
          set({
            sessionId,
            ...data,
            completedSteps: new Set(),
            // Reset surfboard data for new sessions - server will provide if already exists
            surfboardOrderId: isNewSession ? (data.surfboardOrderId || null) : get().surfboardOrderId,
            surfboardNonce: isNewSession ? (data.surfboardNonce || null) : get().surfboardNonce,
            publicKey: isNewSession ? (data.publicKey || null) : get().publicKey,
          });
          
          // Clear sessionStorage if session ID changed to prevent stale data
          if (isNewSession) {
            sessionStorage.removeItem('sagapay-checkout');
          }
        },
        
        setSurfboardData: (orderId, nonce, publicKey) => {
          set({
            surfboardOrderId: orderId,
            surfboardNonce: nonce,
            publicKey,
          });
        },
        
        // Navigation
        setStep: (step) => {
          set({ currentStep: step });
          // onStepChange existed on the config and was wired to STEP_CHANGED,
          // but nothing ever emitted it — so the callback was documented,
          // accepted, and dead. Every transition goes through these three.
          notifyParent('STEP_CHANGED', { step });
        },
        
        nextStep: () => {
          const { currentStep, skipShipping, selectedShipping } = get();
          const currentIndex = STEP_ORDER.indexOf(currentStep);
          if (currentIndex < STEP_ORDER.length - 1) {
            let nextIndex = currentIndex + 1;
            // Skip shipping step if skipShipping is true
            if (STEP_ORDER[nextIndex] === 'shipping' && skipShipping) {
              // Set a free shipping option before skipping
              if (!selectedShipping) {
                set({
                  selectedShipping: {
                    id: 'digital',
                    provider: 'none',
                    method: 'digital',
                    displayName: 'Digital levering',
                    description: 'Ingen fysisk levering',
                    price: { amount: 0, currency: 'NOK' },
                    deliveryDays: 0,
                    isPickup: false,
                  }
                });
              }
              nextIndex++; // Skip to payment
            }
            if (nextIndex < STEP_ORDER.length) {
              set({ currentStep: STEP_ORDER[nextIndex] });
              notifyParent('STEP_CHANGED', { step: STEP_ORDER[nextIndex] });
            }
          }
        },
        
        prevStep: () => {
          const { currentStep, skipShipping } = get();
          const currentIndex = STEP_ORDER.indexOf(currentStep);
          if (currentIndex > 0) {
            let prevIndex = currentIndex - 1;
            // Skip shipping step if skipShipping is true when going back
            if (STEP_ORDER[prevIndex] === 'shipping' && skipShipping) {
              prevIndex--; // Skip to address
            }
            if (prevIndex >= 0) {
              set({ currentStep: STEP_ORDER[prevIndex] });
              notifyParent('STEP_CHANGED', { step: STEP_ORDER[prevIndex] });
            }
          }
        },
        
        completeStep: (step) => {
          const { completedSteps } = get();
          const newCompleted = new Set(completedSteps);
          newCompleted.add(step);
          set({ completedSteps: newCompleted });
        },
        
        // Customer
        setCustomer: (customer) => set({ customer }),
        
        // Address
        setShippingAddress: (address) => set({ shippingAddress: address }),
        setBillingAddress: (address) => set({ billingAddress: address }),
        setUseSameAddress: (same) => set({ useSameAddress: same }),
        setSavedAddresses: (addresses) => set({ savedAddresses: addresses }),
        
        // Shipping
        setShippingOptions: (options) => set({ shippingOptions: options }),
        selectShipping: (option) => set({ selectedShipping: option }),
        setCustomShippingOptions: (options) => set({ customShippingOptions: options }),
        setUseBuiltInShipping: (use) => set({ useBuiltInShipping: use }),
        setSkipShipping: (skip) => set({ skipShipping: skip }),
        
        // Payment
        setPaymentMethods: (methods) => set({ paymentMethods: methods }),
        selectPaymentMethod: (method) => set({ selectedPaymentMethod: method }),
        setPaymentResult: (result) => set({ paymentResult: result }),
        
        // Theming
        setTheme: (theme) => set({ theme }),
        
        // Cart
        setCart: (cart) => set({ cart }),
        
        // Merchant
        setMerchant: (merchant) => set({ merchant }),
        
        // Locale
        setLocale: (locale) => set({ locale }),
        
        // UI
        setLoading: (loading) => set({ isLoading: loading }),
        setError: (error) => set({ error }),
        setPaymentNotice: (paymentNotice) => set({ paymentNotice }),
        setDraft: (patch) => set((state) => ({ draft: { ...state.draft, ...patch } })),
        
        // Quick Pay
        setDeviceToken: (token) => set({ deviceToken: token }),
        setHasQuickPay: (has) => set({ hasQuickPay: has }),
        
        // Complete checkout - verify payment, send webhook to merchant, notify parent
        completeCheckout: async (result) => {
          const state = get();
          
          // If successful, call backend to verify and send merchant webhook
          if (result.success && state.sessionId) {
            try {
              // Dynamic import to avoid circular dependency
              const { completeSession } = await import('../api/client');

              // Retried, because the SDK reports the payment before the API
              // has finished recording it. Measured on a real card: the
              // success screen was up while /complete still answered
              // PAYMENT_NOT_COMPLETE, so the session stayed open and the
              // merchant's webhook waited for the hourly sweep instead of
              // arriving in seconds.
              const waits = [0, 1200, 2500, 4000];
              for (const wait of waits) {
                if (wait) await new Promise((r) => setTimeout(r, wait));

                const response = await completeSession(state.sessionId);
                if (!response.error) break;

                // Anything other than "not settled yet" will not improve.
                if (response.error.code !== 'PAYMENT_NOT_COMPLETE') break;
              }
            } catch (e) {
              // Non-critical: reconciliation picks the session up regardless.
            }
          }
          
          // Build complete result
          const completeResult: CheckoutCompleteResult = {
            success: result.success,
            paymentId: result.paymentId,
            orderId: result.orderId,
            sessionId: state.sessionId || '',
            status: result.status,
            paymentMethod: result.paymentMethod,
            amount: {
              total: (state.cart?.totalAmount.amount || 0) + (state.selectedShipping?.price.amount || 0),
              subtotal: state.cart?.totalAmount.amount || 0,
              shipping: state.selectedShipping?.price.amount || 0,
              currency: state.cart?.totalAmount.currency || 'NOK',
            },
            customer: {
              email: state.customer?.email || '',
              name: state.customer?.name || '',
              phone: state.customer?.phone,
            },
            shippingAddress: {
              streetAddress: state.shippingAddress?.streetAddress || '',
              postalCode: state.shippingAddress?.postalCode || '',
              city: state.shippingAddress?.city || '',
              countryCode: state.shippingAddress?.countryCode || 'NO',
            },
            shipping: {
              id: state.selectedShipping?.id || '',
              name: state.selectedShipping?.displayName || '',
              provider: state.selectedShipping?.provider || '',
              price: state.selectedShipping?.price.amount || 0,
              estimatedDelivery: state.selectedShipping?.estimatedDelivery?.display || '',
            },
            completedAt: new Date().toISOString(),
            errorMessage: result.errorMessage,
            card: result.card,
          };
          
          set({ paymentResult: result });
          
          // Notify parent/callbacks
          if (result.success) {
            notifyParent('CHECKOUT_COMPLETE', completeResult);
          } else {
            notifyParent('CHECKOUT_ERROR', {
              code: result.status,
              message: result.errorMessage || 'Payment failed',
            });
          }
        },
        
        // Reset
        reset: () => set(initialState),
      }),
      {
        name: 'sagapay-checkout',
        storage: {
          getItem: (name) => {
            // Use sessionStorage for session data (clears on tab close)
            const sessionData = sessionStorage.getItem(name);
            // Use localStorage for device preferences (persists)
            const localData = localStorage.getItem(`${name}-local`);
            
            if (!sessionData && !localData) return null;
            
            const session = sessionData ? JSON.parse(sessionData) : { state: {} };
            const local = localData ? JSON.parse(localData) : { state: {} };
            
            // Merge: session data + local preferences
            return {
              ...session,
              state: {
                ...session.state,
                ...local.state,
              },
            };
          },
          setItem: (name, value) => {
            const state = value.state || {};
            
            // Session-specific data (current checkout) -> sessionStorage
            const sessionState = {
              sessionId: state.sessionId,
              currentStep: state.currentStep,
              customer: state.customer,
              shippingAddress: state.shippingAddress,
              billingAddress: state.billingAddress,
              selectedShipping: state.selectedShipping,
              surfboardOrderId: state.surfboardOrderId,
              surfboardNonce: state.surfboardNonce,
              publicKey: state.publicKey,
              // What the customer has typed but not submitted. sessionStorage
              // and not localStorage on purpose: it survives the reload it
              // exists for, and a name, phone and address do not outlive the
              // tab they were typed into.
              draft: state.draft,
            };
            sessionStorage.setItem(name, JSON.stringify({ ...value, state: sessionState }));
            
            // User preferences (persist across sessions) -> localStorage
            const localState = {
              deviceToken: state.deviceToken,
            };
            localStorage.setItem(`${name}-local`, JSON.stringify({ state: localState }));
          },
          removeItem: (name) => {
            sessionStorage.removeItem(name);
            localStorage.removeItem(`${name}-local`);
          },
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        partialize: (state) => ({
          // Only persist these fields total
          sessionId: state.sessionId,
          currentStep: state.currentStep,
          customer: state.customer,
          shippingAddress: state.shippingAddress,
          billingAddress: state.billingAddress,
          selectedShipping: state.selectedShipping,
          surfboardOrderId: state.surfboardOrderId,
          surfboardNonce: state.surfboardNonce,
          publicKey: state.publicKey,
          deviceToken: state.deviceToken,
          draft: state.draft,
        }) as any,
      }
    ),
    { name: 'CheckoutStore' }
  )
);

// =====================================================
// SELECTORS
// =====================================================

export const useCurrentStep = () => useCheckoutStore((s) => s.currentStep);
export const useIsLoading = () => useCheckoutStore((s) => s.isLoading);
export const useError = () => useCheckoutStore((s) => s.error);
export const useCart = () => useCheckoutStore((s) => s.cart);
export const useMerchant = () => useCheckoutStore((s) => s.merchant);
export const useLocale = () => useCheckoutStore((s) => s.locale);
export const useCustomer = () => useCheckoutStore((s) => s.customer);
export const useShippingAddress = () => useCheckoutStore((s) => s.shippingAddress);
export const useSelectedShipping = () => useCheckoutStore((s) => s.selectedShipping);
export const usePaymentMethods = () => useCheckoutStore((s) => s.paymentMethods);

// Computed: Total with shipping
export const useTotalWithShipping = () => {
  const cart = useCheckoutStore((s) => s.cart);
  const shipping = useCheckoutStore((s) => s.selectedShipping);
  
  if (!cart) return null;
  
  const shippingAmount = shipping?.price.amount || 0;
  return {
    amount: cart.totalAmount.amount + shippingAmount,
    currency: cart.totalAmount.currency,
  };
};

// Computed: Progress percentage
export const useProgressPercentage = () => {
  const currentStep = useCheckoutStore((s) => s.currentStep);
  const stepIndex = STEP_ORDER.indexOf(currentStep);
  return Math.round(((stepIndex + 1) / STEP_ORDER.length) * 100);
};

// Computed: Can proceed to next step
export const useCanProceed = () => {
  const state = useCheckoutStore();
  
  switch (state.currentStep) {
    case 'identity':
      return !!state.customer?.email && !!state.customer?.name;
    case 'address':
      return !!state.shippingAddress?.streetAddress && !!state.shippingAddress?.postalCode;
    case 'shipping':
      return !!state.selectedShipping;
    case 'payment':
      return !!state.selectedPaymentMethod;
    default:
      return false;
  }
};
